import { useMemo, useRef, useState } from 'react';
import { Download, FileText, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FolderUploadButton } from '@/components/ui/FolderUploadButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { MobileListCard, MobileListCardStack } from '@/components/ui/MobileListCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { EditableText } from '@/components/inventory/EditableItemField';
import { RemoveIconButton } from '@/components/equipment/DetailRows';
import {
  useDeleteEquipmentFile,
  useEquipmentFileUrl,
  useEquipmentFiles,
  useRenameEquipmentFile,
  useUploadEquipmentFile,
  type Equipment,
  type EquipmentFile,
} from '@/features/equipment/hooks';
import {
  EQUIPMENT_DOCUMENT_KINDS,
  EQUIPMENT_FILE_KIND_LABELS,
  MAX_EQUIPMENT_FILE_BYTES,
  type EquipmentFileKind,
} from '@/features/equipment/model';
import { formatDate } from '@/lib/formatters';
import { useIsMobile } from '@/lib/useMediaQuery';
import { cn } from '@/lib/utils';

type DocumentKind = (typeof EQUIPMENT_DOCUMENT_KINDS)[number];

export function EquipmentDocumentsTab({
  equipment,
  canEdit,
}: {
  equipment: Equipment;
  canEdit: boolean;
}) {
  const isMobile = useIsMobile();
  const { data: files, isLoading } = useEquipmentFiles(equipment.id);
  const upload = useUploadEquipmentFile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadKind, setUploadKind] = useState<DocumentKind>('manual');
  const [filter, setFilter] = useState<DocumentKind | 'all'>('all');

  const documents = useMemo(() => (files ?? []).filter((f) => f.kind !== 'photo'), [files]);
  const counts = useMemo(() => {
    const map = new Map<EquipmentFileKind, number>();
    for (const d of documents) map.set(d.kind, (map.get(d.kind) ?? 0) + 1);
    return map;
  }, [documents]);
  const visible = filter === 'all' ? documents : documents.filter((d) => d.kind === filter);

  const handleFiles = async (list: FileList | File[] | null) => {
    if (!list || list.length === 0) return;
    setUploading(true);
    let uploaded = 0;
    try {
      for (const file of Array.from(list)) {
        if (file.size > MAX_EQUIPMENT_FILE_BYTES) {
          toast.error(`“${file.name}” is over the 100 MB limit.`);
          continue;
        }
        await upload.mutateAsync({ equipmentId: equipment.id, file, kind: uploadKind });
        uploaded += 1;
      }
      if (uploaded > 0) {
        toast.success(uploaded === 1 ? 'Document uploaded' : `${uploaded} documents uploaded`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle>Manuals & documents</CardTitle>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={uploadKind} onValueChange={(v) => setUploadKind(v as DocumentKind)}>
                <SelectTrigger className="h-9 w-[200px] text-sm" aria-label="Document type">
                  {EQUIPMENT_FILE_KIND_LABELS[uploadKind]}
                </SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_DOCUMENT_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {EQUIPMENT_FILE_KIND_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-9"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Paperclip className="h-3.5 w-3.5" />
                )}
                {uploading ? 'Uploading…' : 'Add documents'}
              </Button>
              <FolderUploadButton
                size="sm"
                className="h-9"
                disabled={uploading}
                onFiles={(files) => void handleFiles(files)}
              />
              <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => void handleFiles(e.target.files)}
              />
            </div>
          )}
        </div>
        {documents.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2" role="group" aria-label="Filter by type">
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              All <span className="tabular-nums opacity-70">{documents.length}</span>
            </FilterChip>
            {EQUIPMENT_DOCUMENT_KINDS.filter((k) => counts.has(k)).map((k) => (
              <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)}>
                {EQUIPMENT_FILE_KIND_LABELS[k]}{' '}
                <span className="tabular-nums opacity-70">{counts.get(k)}</span>
              </FilterChip>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : documents.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No documents yet"
            action={
              canEdit ? (
                <Button variant="outline" onClick={() => inputRef.current?.click()}>
                  <Paperclip className="h-4 w-4" />
                  Upload a manual, warranty or invoice
                </Button>
              ) : undefined
            }
          />
        ) : isMobile ? (
          <MobileListCardStack>
            {visible.map((doc) => (
              <MobileDocumentRow key={doc.id} doc={doc} />
            ))}
          </MobileListCardStack>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-px" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  canEdit={canEdit}
                  coverPath={equipment.image_path}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex min-h-9 items-center gap-1 rounded-full border px-3 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-card text-foreground hover:bg-secondary',
      )}
    >
      {children}
    </button>
  );
}

function DocumentRow({
  doc,
  canEdit,
  coverPath,
}: {
  doc: EquipmentFile;
  canEdit: boolean;
  coverPath: string | null;
}) {
  const { data: url, isLoading } = useEquipmentFileUrl(doc.storage_path);
  const rename = useRenameEquipmentFile();
  const remove = useDeleteEquipmentFile();

  const onDelete = async () => {
    if (!window.confirm(`Delete “${doc.label}”? This can't be undone.`)) return;
    try {
      await remove.mutateAsync({ file: doc, coverPath });
      toast.success('Document deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <TableRow>
      <TableCell className="max-w-[28rem] font-medium">
        <EditableText
          value={doc.label}
          required
          maxLength={200}
          ariaLabel="Document name"
          canEdit={canEdit}
          density="cell"
          onSave={async (next) => {
            await rename.mutateAsync({ id: doc.id, equipmentId: doc.equipment_id, label: next });
          }}
        />
      </TableCell>
      <TableCell>
        <Badge variant="outline">{EQUIPMENT_FILE_KIND_LABELS[doc.kind] ?? doc.kind}</Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {formatBytes(doc.size_bytes)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        {formatDate(doc.created_at)}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-1">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              download={doc.label}
              className="inline-flex min-h-9 items-center gap-1 px-2 text-xs text-atmosphere-dark hover:underline"
            >
              Download
              <Download className="h-3 w-3" />
            </a>
          ) : isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : null}
          {canEdit && (
            <RemoveIconButton
              label={`Delete ${doc.label}`}
              onClick={() => void onDelete()}
              disabled={remove.isPending}
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function MobileDocumentRow({ doc }: { doc: EquipmentFile }) {
  const { data: url } = useEquipmentFileUrl(doc.storage_path);
  return (
    <MobileListCard
      title={doc.label}
      subtitle={[formatBytes(doc.size_bytes), formatDate(doc.created_at)].join(' · ')}
      badges={<Badge variant="outline">{EQUIPMENT_FILE_KIND_LABELS[doc.kind] ?? doc.kind}</Badge>}
      onTap={url ? () => window.open(url, '_blank', 'noopener') : undefined}
      trailing={<Download className="h-4 w-4 text-muted-foreground" aria-hidden />}
    />
  );
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
